<template>
  <div class="rzt-page py-3">
    <header class="rzt-toolbar">
      <div class="rzt-toolbar__title">
        <span class="rzt-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Razorpay Transactions</h1>
          <p>Every payment Razorpay captured — card, UPI, netbanking &amp; wallet</p>
        </div>
      </div>
      <div class="rzt-toolbar__actions">
        <button
          type="button"
          class="rzt-btn rzt-btn--ghost"
          :disabled="loading"
          title="Reload"
          @click="loadTransactions(1)"
        >
          <svg class="rzt-refresh-icon" :class="{ 'is-spinning': loading }" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          <span>Refresh</span>
        </button>
        <template v-if="syncing">
          <button type="button" class="rzt-btn rzt-btn--primary" disabled>
            Syncing {{ syncProgress.current }}/{{ syncProgress.total }}…
          </button>
          <button type="button" class="rzt-btn rzt-btn--ghost" @click="cancelSync">Stop</button>
        </template>
        <button
          v-else
          type="button"
          class="rzt-btn rzt-btn--primary"
          @click="syncFromRazorpay"
        >
          Sync from Razorpay
        </button>
      </div>
    </header>

    <div v-if="syncing" class="rzt-progress" :title="`Window ${syncProgress.current} of ${syncProgress.total}`">
      <div class="rzt-progress__bar" :style="{ width: syncPct + '%' }"></div>
    </div>

    <CAlert v-if="errorMessage" color="danger" show class="rzt-alert" @click.native="errorMessage = ''">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="rzt-alert" @click.native="successMessage = ''">{{ successMessage }}</CAlert>

    <!-- ═══════════════ KPI CARDS ═══════════════ -->
    <section class="rzt-kpi-row">
      <div class="rzt-kpi rzt-kpi--emerald">
        <div class="rzt-kpi__label">Captured Amount</div>
        <div class="rzt-kpi__value">{{ formatINR(totals.captured_amount) }}</div>
      </div>
      <div class="rzt-kpi rzt-kpi--indigo">
        <div class="rzt-kpi__label">Transactions</div>
        <div class="rzt-kpi__value">{{ totals.count }}</div>
      </div>
      <div class="rzt-kpi rzt-kpi--amber">
        <div class="rzt-kpi__label">Razorpay Fees</div>
        <div class="rzt-kpi__value">{{ formatINR(totals.total_fee) }}</div>
      </div>
      <div class="rzt-kpi rzt-kpi--violet">
        <div class="rzt-kpi__label">Net Settled</div>
        <div class="rzt-kpi__value">{{ formatINR(totals.net_amount) }}</div>
      </div>
    </section>

    <!-- ═══════════════ FILTERS ═══════════════ -->
    <section class="rzt-section">
      <header class="rzt-section__head">
        <h2>Filters</h2>
      </header>
      <div class="rzt-section__body">
        <div class="rzt-presets">
          <span class="rzt-presets__label">Quick range</span>
          <button
            v-for="p in presets"
            :key="p.key"
            type="button"
            :class="['rzt-chip', { 'is-active': activePreset === p.key }]"
            @click="applyPreset(p)"
          >{{ p.label }}</button>
        </div>
        <div class="rzt-filters">
          <div class="rzt-filter">
            <label class="rzt-label">From Date</label>
            <SingleDatePicker :value="filters.from" @input="onDateInput('from', $event)" />
          </div>
          <div class="rzt-filter">
            <label class="rzt-label">To Date</label>
            <SingleDatePicker :value="filters.to" @input="onDateInput('to', $event)" />
          </div>
          <div class="rzt-filter">
            <label class="rzt-label">Status</label>
            <AppDropdown :value="filters.status" :options="statusOptions" @input="filters.status = $event" />
          </div>
          <div class="rzt-filter">
            <label class="rzt-label">Method</label>
            <AppDropdown :value="filters.method" :options="methodOptions" @input="filters.method = $event" />
          </div>
          <div class="rzt-filter rzt-filter--search">
            <label class="rzt-label">Search</label>
            <AppInput
              no-margin
              :value="filters.search"
              placeholder="Payment ID, email, phone, UPI…"
              @update:value="filters.search = $event"
              @keyup.enter.native="loadTransactions(1)"
            />
          </div>
          <div class="rzt-filter rzt-filter--action">
            <label class="rzt-label" aria-hidden="true">&nbsp;</label>
            <div class="rzt-action-group">
              <button type="button" class="rzt-btn rzt-btn--primary" :disabled="loading" @click="loadTransactions(1)">
                {{ loading ? 'Loading…' : 'Apply' }}
              </button>
              <button type="button" class="rzt-btn rzt-btn--ghost" :disabled="loading" @click="resetFilters">
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ TABLE ═══════════════ -->
    <BaseTable
      title="Transactions"
      :items="tableRows"
      :columns="columns"
      :loading="loading"
      :sortable="false"
      :show-count="false"
      empty-text="No transactions for the selected filters. Try widening the date range or hit ‘Sync from Razorpay’."
    >
      <template #toolbar>
        <span class="rzt-count-hint">{{ rangeLabel }}</span>
      </template>
      <template #cell-customer="{ row }">
        <div class="rzt-customer">
          <span class="rzt-customer__primary">{{ row.customer_primary }}</span>
          <span v-if="row.customer_phone" class="rzt-customer__phone">{{ row.customer_phone }}</span>
        </div>
      </template>
      <template #cell-method="{ value }">
        <span :class="['rzt-pill', methodClass(value)]">{{ formatLabel(value) }}</span>
      </template>
      <template #cell-status="{ value }">
        <span :class="['rzt-pill', statusClass(value)]">{{ formatLabel(value) }}</span>
      </template>
      <template #cell-amount="{ value }">{{ formatINR(value) }}</template>
      <template #cell-fee="{ value }">{{ formatINR(value) }}</template>
    </BaseTable>

    <div v-if="!loading && tableRows.length && lastPage > 1" class="rzt-pagination-bar">
      <span class="rzt-pagination-info">{{ rangeLabel }}</span>
      <AppPager :current-page="currentPage" :last-page="lastPage" @change="loadTransactions" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'

function isoToday () {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function daysAgo (n) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function toISO (d) {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function parseISO (s) {
  const [y, m, d] = String(s).split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

function monthsAgo (n) {
  const d = new Date()
  d.setMonth(d.getMonth() - n)
  return toISO(d)
}

// Indian financial year start (1 April). Before April, the FY started last year.
function fyStart () {
  const d = new Date()
  const startYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1
  return `${startYear}-04-01`
}

// Earliest date "All time" reaches back to (mirrors the 2020 floor used
// elsewhere in the finance views). Adjust if the Razorpay account predates this.
const ALL_TIME_FLOOR = '2020-01-01'

// Each sync request covers at most this many days, so no single call walks too
// many Razorpay pages or risks a gateway timeout.
const SYNC_CHUNK_DAYS = 31

// Build [from, to] windows spanning the range, newest first.
function buildWindows (fromStr, toStr, chunkDays) {
  const windows = []
  const start = parseISO(fromStr)
  let end = parseISO(toStr)
  let guard = 0
  while (end >= start && guard < 1000) {
    guard++
    let winStart = new Date(end)
    winStart.setDate(winStart.getDate() - (chunkDays - 1))
    if (winStart < start) winStart = new Date(start)
    windows.push({ from: toISO(winStart), to: toISO(end) })
    end = new Date(winStart)
    end.setDate(end.getDate() - 1)
  }
  return windows
}

function blankTotals () {
  return {
    count: 0,
    captured_count: 0,
    total_amount: 0,
    captured_amount: 0,
    total_fee: 0,
    total_refunded: 0,
    net_amount: 0
  }
}

export default {
  name: 'RazorpayTransactions',
  components: { AppDropdown, AppInput, SingleDatePicker, BaseTable, AppPager },
  data () {
    return {
      loading: false,
      syncing: false,
      activePreset: 'd30',
      syncProgress: { current: 0, total: 0, cancel: false },
      errorMessage: '',
      successMessage: '',
      rows: [],
      totals: blankTotals(),
      currentPage: 1,
      lastPage: 1,
      total: 0,
      perPage: 25,
      filters: {
        from: daysAgo(30),
        to: isoToday(),
        status: 'all',
        method: 'all',
        search: ''
      },
      presets: [
        { key: 'd7', label: '7D', from: () => daysAgo(7) },
        { key: 'd30', label: '30D', from: () => daysAgo(30) },
        { key: 'd90', label: '90D', from: () => daysAgo(90) },
        { key: 'm6', label: '6M', from: () => monthsAgo(6) },
        { key: 'y1', label: '1Y', from: () => monthsAgo(12) },
        { key: 'fy', label: 'This FY', from: () => fyStart() },
        { key: 'all', label: 'All', from: () => ALL_TIME_FLOOR }
      ],
      statusOptions: [
        { value: 'all', label: 'All Statuses' },
        { value: 'captured', label: 'Captured' },
        { value: 'authorized', label: 'Authorized' },
        { value: 'failed', label: 'Failed' },
        { value: 'refunded', label: 'Refunded' }
      ],
      methodOptions: [
        { value: 'all', label: 'All Methods' },
        { value: 'upi', label: 'UPI' },
        { value: 'card', label: 'Card' },
        { value: 'netbanking', label: 'Netbanking' },
        { value: 'wallet', label: 'Wallet' },
        { value: 'emi', label: 'EMI' }
      ],
      columns: [
        { key: 'created_display', label: 'Date', width: '150px' },
        { key: 'razorpay_payment_id', label: 'Payment ID', width: '180px' },
        { key: 'customer', label: 'Customer' },
        { key: 'method', label: 'Method', width: '110px' },
        { key: 'amount', label: 'Amount', align: 'right', width: '120px' },
        { key: 'fee', label: 'Fee', align: 'right', width: '100px' },
        { key: 'status', label: 'Status', width: '120px' },
        { key: 'razorpay_order_id', label: 'Order', width: '170px' }
      ]
    }
  },
  computed: {
    tableRows () {
      return (this.rows || []).map((r) => {
        const primary = r.email || r.vpa || r.contact || '—'
        return {
          ...r,
          created_display: this.formatDateTime(r.rzp_created_at),
          customer_primary: primary,
          // Show the phone on its own line, unless it's already the primary line.
          customer_phone: (r.contact && r.contact !== primary) ? r.contact : '',
          razorpay_order_id: r.razorpay_order_id || '—'
        }
      })
    },
    rangeLabel () {
      if (!this.total) return '0 transactions'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = Math.min(this.currentPage * this.perPage, this.total)
      return `Showing ${start}–${end} of ${this.total}`
    },
    syncPct () {
      if (!this.syncProgress.total) return 0
      return Math.round((this.syncProgress.current / this.syncProgress.total) * 100)
    }
  },
  mounted () {
    this.loadTransactions(1)
  },
  methods: {
    formatINR (value) {
      const n = Number(value)
      if (Number.isNaN(n)) return '—'
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n)
    },
    formatLabel (value) {
      if (value === null || value === undefined || value === '') return '—'
      return String(value).replace(/_/g, ' ')
    },
    formatDateTime (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '—'
      const day = String(d.getDate()).padStart(2, '0')
      const mon = d.toLocaleString('en-US', { month: 'short' })
      const time = d.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
      return `${day} ${mon}, ${time}`
    },
    statusClass (value) {
      const s = String(value || '').toLowerCase()
      if (s === 'captured') return 'rzt-pill--green'
      if (s === 'authorized') return 'rzt-pill--blue'
      if (s === 'refunded') return 'rzt-pill--amber'
      if (s === 'failed') return 'rzt-pill--red'
      return 'rzt-pill--gray'
    },
    methodClass (value) {
      const s = String(value || '').toLowerCase()
      if (s === 'upi') return 'rzt-pill--violet'
      if (s === 'card') return 'rzt-pill--blue'
      if (s === 'netbanking') return 'rzt-pill--teal'
      if (s === 'wallet') return 'rzt-pill--amber'
      return 'rzt-pill--gray'
    },
    parseError (error, fallback) {
      return error?.response?.data?.message || fallback
    },
    onDateInput (which, value) {
      this.filters[which] = value
      this.activePreset = ''   // manual edit clears the quick-range highlight
    },
    applyPreset (preset) {
      this.activePreset = preset.key
      this.filters.from = preset.from()
      this.filters.to = isoToday()
      this.loadTransactions(1)
    },
    resetFilters () {
      this.filters = {
        from: daysAgo(30),
        to: isoToday(),
        status: 'all',
        method: 'all',
        search: ''
      }
      this.activePreset = 'd30'
      this.loadTransactions(1)
    },
    loadTransactions (page = 1) {
      if (!this.filters.from || !this.filters.to) {
        this.errorMessage = 'From Date and To Date are required.'
        return
      }
      this.errorMessage = ''
      this.loading = true
      const params = {
        from: this.filters.from,
        to: this.filters.to,
        status: this.filters.status,
        method: this.filters.method,
        search: this.filters.search || undefined,
        per_page: this.perPage,
        page
      }
      axios.get(`${this.$apiAdress}/api/razorpay-transactions`, { params })
        .then((response) => {
          const data = response.data || {}
          this.rows = Array.isArray(data.data) ? data.data : []
          this.totals = { ...blankTotals(), ...(data.totals || {}) }
          this.currentPage = Number(data.current_page || 1)
          this.lastPage = Number(data.last_page || 1)
          this.total = Number(data.total || 0)
          this.perPage = Number(data.per_page || this.perPage)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load Razorpay transactions.')
          this.rows = []
          this.totals = blankTotals()
          this.total = 0
          this.lastPage = 1
        })
        .finally(() => { this.loading = false })
    },
    cancelSync () {
      this.syncProgress.cancel = true
    },
    // Sync the selected range in newest-first monthly windows. Each window is a
    // bounded request (no gateway timeout on wide ranges), the table refreshes
    // as data arrives, and the user can Stop midway.
    async syncFromRazorpay () {
      if (!this.filters.from || !this.filters.to) {
        this.errorMessage = 'From Date and To Date are required.'
        return
      }
      this.errorMessage = ''
      this.successMessage = ''
      const windows = buildWindows(this.filters.from, this.filters.to, SYNC_CHUNK_DAYS)
      this.syncProgress = { current: 0, total: windows.length, cancel: false }
      this.syncing = true

      let totalUpserted = 0
      let failed = 0
      for (let i = 0; i < windows.length; i++) {
        if (this.syncProgress.cancel) break
        this.syncProgress.current = i + 1
        try {
          const response = await axios.post(
            `${this.$apiAdress}/api/razorpay-transactions/sync`,
            windows[i],
            { timeout: 180000 }
          )
          totalUpserted += Number(response?.data?.upserted || 0)
        } catch (error) {
          console.log(error)
          failed++
        }
        // Surface incoming rows progressively (newest windows first).
        this.loadTransactions(1)
      }

      this.syncing = false
      const stopped = this.syncProgress.cancel
      if (failed && !totalUpserted) {
        this.errorMessage = 'Sync from Razorpay failed. Check the date range and try again.'
      } else {
        const scope = stopped ? `${this.syncProgress.current - 1} of ${windows.length}` : `${windows.length}`
        this.successMessage = `${stopped ? 'Stopped — ' : ''}Synced ${totalUpserted} transaction(s) across ${scope} window(s)`
          + (failed ? ` (${failed} window(s) errored).` : '.')
      }
      this.loadTransactions(1)
    }
  }
}
</script>

<style scoped>
.rzt-page { padding: 0.25rem 0; }

.rzt-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.rzt-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }

.rzt-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.rzt-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.rzt-toolbar__title p { margin: 0; font-size: 0.78rem; color: #6b7280; }

.rzt-toolbar__actions { display: flex; align-items: center; gap: 0.5rem; }

.rzt-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
  cursor: pointer;
}

/* ── KPI cards ── */
.rzt-kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.rzt-kpi {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-left: 4px solid transparent;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.rzt-kpi__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b778d;
  margin-bottom: 0.25rem;
}

.rzt-kpi__value {
  font-size: 1.45rem;
  font-weight: 700;
  color: #18233a;
  line-height: 1.1;
}

.rzt-kpi--emerald { border-left-color: #1b8f5a; }
.rzt-kpi--emerald .rzt-kpi__value { color: #0e5b46; }
.rzt-kpi--indigo  { border-left-color: #3f51b5; }
.rzt-kpi--amber   { border-left-color: #d97706; }
.rzt-kpi--violet  { border-left-color: #6a1b9a; }

@media (max-width: 992px) {
  .rzt-kpi-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 480px) {
  .rzt-kpi-row { grid-template-columns: minmax(0, 1fr); }
}

/* ── Section card ── */
.rzt-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.rzt-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.rzt-section__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; }

.rzt-section__body { padding: 1rem; }

/* ── Quick-range presets ── */
.rzt-presets {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px dashed #eef1f7;
}

.rzt-presets__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b778d;
  margin-right: 0.35rem;
}

.rzt-chip {
  height: 28px;
  padding: 0 0.75rem;
  border-radius: 999px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #475569;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rzt-chip:focus { outline: none; }
.rzt-chip::-moz-focus-inner { border: 0; }
.rzt-chip:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.rzt-chip:hover:not(.is-active) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }

.rzt-chip.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

/* ── Sync progress bar ── */
.rzt-progress {
  height: 4px;
  border-radius: 999px;
  background: #e5e9f2;
  overflow: hidden;
  margin: 0 0.25rem 0.85rem;
}

.rzt-progress__bar {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #0e5b46 0%, #16a07a 100%);
  transition: width 0.3s ease;
}

.rzt-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.rzt-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 150px;
  min-width: 0;
}

/* Search soaks up the leftover width so the row fills edge-to-edge
   instead of leaving an empty gap before the action buttons. */
.rzt-filter--search { flex: 2 1 240px; }

.rzt-filter--action { flex: 0 0 auto; min-width: 0; }

.rzt-action-group { display: flex; gap: 0.5rem; }

.rzt-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

/* ── Buttons ── */
.rzt-btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.rzt-btn:focus { outline: none; }
.rzt-btn::-moz-focus-inner { border: 0; }
.rzt-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.rzt-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.rzt-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.rzt-btn--ghost {
  background: #ffffff;
  border: 1px solid #d8dde6;
  color: #1f2a44;
}

.rzt-btn--ghost:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
}

.rzt-btn:disabled { opacity: 0.7; cursor: progress; }

.rzt-refresh-icon { transition: transform 0.3s ease; }
.rzt-btn--ghost:hover:not(:disabled) .rzt-refresh-icon { transform: rotate(60deg); }
.rzt-refresh-icon.is-spinning { animation: rzt-spin 0.8s linear infinite; }

@keyframes rzt-spin { to { transform: rotate(360deg); } }

/* ── Table extras ── */
.rzt-count-hint { font-size: 0.78rem; color: #6b7280; }

.rzt-customer { display: flex; flex-direction: column; line-height: 1.25; }
.rzt-customer__primary { color: #1f2a44; }
.rzt-customer__phone { font-size: 0.72rem; color: #6b7280; }

.rzt-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.67rem;
  font-weight: 700;
  white-space: nowrap;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}

.rzt-pill--green  { background: #dff5ea; color: #0e5b46; }
.rzt-pill--blue   { background: #dbeafe; color: #1e40af; }
.rzt-pill--violet { background: #ede9fe; color: #5b21b6; }
.rzt-pill--teal   { background: #ccfbf1; color: #0f766e; }
.rzt-pill--amber  { background: #fef3c7; color: #92400e; }
.rzt-pill--red    { background: #fee2e2; color: #991b1b; }
.rzt-pill--gray   { background: #f1f5f9; color: #475569; }

.rzt-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.85rem;
}

.rzt-pagination-info { font-size: 0.78rem; color: #6b7280; }

@media (max-width: 576px) {
  .rzt-filter--action { flex: 1 1 100%; }
  .rzt-action-group { width: 100%; }
  .rzt-action-group .rzt-btn { flex: 1 1 0; justify-content: center; }
}
</style>
